import { type ComponentProps } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon.tsx'

/**
 * Spinner — a standalone inline loading indicator: the `update` icon spinning,
 * wrapped in a `role="status"` region so assistive tech announces the busy
 * state. The rotation is dropped under `prefers-reduced-motion`
 * (`motion-reduce:animate-none`).
 *
 * Extracted from StatusButton (which now composes it) so loading no longer
 * lives only inside the button — it backs any loading/empty state (e.g. the
 * CommandPalette async results). It spins in the brand accent; pass
 * `text-current` to take the surrounding text colour, as StatusButton does.
 * Tokens only.
 */
const spinnerSizes = { default: 'size-5', sm: 'size-4' }

function Spinner({
	className,
	title = 'loading',
	size = 'default',
	...props
}: ComponentProps<'div'> & {
	title?: string
	size?: keyof typeof spinnerSizes
}) {
	return (
		<div
			role="status"
			data-slot="spinner"
			className={cn(
				'text-brand inline-flex items-center justify-center',
				spinnerSizes[size],
				className,
			)}
			{...props}
		>
			<Icon
				name="update"
				className="size-full animate-spin motion-reduce:animate-none"
				title={title}
			/>
		</div>
	)
}

export { Spinner }
